/**
 * Frontend companion of the backend AdCreativeInsightContextService
 * (builder-service/src/agent/services/adCreative/AdCreativeInsightContextService.ts).
 *
 * Lists completed insights analyses for the Create-home context selector and
 * derives the audience/pain/job chip PREVIEW from the pinned analysis. The
 * preview intentionally mirrors the backend `deriveAudienceBrief` rules
 * (top-weight fears -> pain, coreJobs/bigJobs -> job, dominant audience);
 * authoritative derivation still happens server-side at generation time —
 * these chips only show the user what the agents will receive.
 */

import { InsightsFirestoreService, type InsightsAnalysisDoc } from '@modules/insights-analyzer';

export interface AdCreativeAnalysisOption {
  id: string;
  label: string;
  insightCount: number;
  updatedAt?: string;
}

export interface AdCreativeDerivedChips {
  audienceName?: string;
  painOrNeed?: string;
  jobToBeDone?: string;
}

/** insightId -> display payload for angle cite blocks / scene hints. */
export interface AdCreativeInsightIndexEntry {
  category: string;
  thesis: string;
  actorCount: number;
  cumulativeWeight: number;
  topQuote?: string;
}

export type AdCreativeInsightIndex = Record<string, AdCreativeInsightIndexEntry>;

const INSIGHT_PATH_RE = /^@insight\[([^\]]+)\]\.([a-zA-Z]+)\[([^\]]+)\]$/;

/** JTBD category -> theme chip color (mirrors DataMappingViewerV2 palette intent). */
export const INSIGHT_CATEGORY_COLOR: Record<string, 'error' | 'success' | 'warning' | 'secondary' | 'info' | 'default'> = {
  fears: 'error',
  coreJobs: 'success',
  bigJobs: 'success',
  habits: 'warning',
  competitors: 'secondary',
  contexts: 'info',
  subtasks: 'info',
};

/**
 * Scene role -> JTBD categories that ground it (mirror of the backend
 * SCENE_ROLE_CATEGORIES map in AdCreativeInsightContextService).
 */
export const SCENE_ROLE_CATEGORIES: Record<string, string[]> = {
  hook: ['fears', 'competitors'],
  problem: ['habits', 'contexts', 'fears'],
  demo: ['coreJobs', 'subtasks', 'bigJobs'],
  benefit: ['coreJobs', 'bigJobs'],
  objection: ['fears', 'competitors'],
};

/** Parse `@insight[analysisId].category[insightId]`; null for other shapes. */
export function parseInsightPath(path: string): { analysisId: string; category: string; insightId: string } | null {
  const match = INSIGHT_PATH_RE.exec(String(path ?? '').trim());
  return match ? { analysisId: match[1], category: match[2], insightId: match[3] } : null;
}

interface AggregatedInsightLike {
  id?: string;
  category?: string;
  canonicalDescription?: string;
  cumulativeWeight?: number;
  actorCount?: number;
  frequency?: number;
  sources?: Array<{
    originalDescription?: string;
    originalWeight?: number;
    actorId?: string;
    audienceId?: string;
    audienceName?: string;
  }>;
}

/** Summary count without materializing insight objects (FR0-I2). */
function countInsightsLight(doc: InsightsAnalysisDoc): number {
  const counts = (doc as { categoryCounts?: Record<string, unknown> }).categoryCounts;
  if (counts && typeof counts === 'object') {
    const total = Object.values(counts)
      .reduce<number>((sum, value) => sum + (typeof value === 'number' ? value : 0), 0);
    if (total > 0) return total;
  }
  const byCategory = doc.aggregatedInsights;
  if (!byCategory || typeof byCategory !== 'object') return 0;
  return Object.values(byCategory)
    .reduce<number>((sum, list) => sum + (Array.isArray(list) ? list.length : 0), 0);
}

function flattenInsights(byCategory: Record<string, unknown> | undefined): AggregatedInsightLike[] {
  if (!byCategory || typeof byCategory !== 'object') return [];
  const flat: AggregatedInsightLike[] = [];
  for (const [category, list] of Object.entries(byCategory)) {
    if (!Array.isArray(list)) continue;
    for (const entry of list) {
      if (entry && typeof entry === 'object' && typeof (entry as AggregatedInsightLike).canonicalDescription === 'string') {
        flat.push({ ...(entry as AggregatedInsightLike), category });
      }
    }
  }
  return flat.sort((a, b) => (b.cumulativeWeight ?? 0) - (a.cumulativeWeight ?? 0));
}

export class AdCreativeInsightContextClient {
  private readonly service: InsightsFirestoreService;

  constructor(projectId: string) {
    this.service = new InsightsFirestoreService(projectId);
  }

  /**
   * Completed analyses newest-first, with a display label and insight count.
   * Light projection (FR0-I2): counts come from persisted category meta or a
   * cheap array-length sum — never from flattening/copying 50 full docs'
   * insight objects on mount. Full-doc parsing stays reserved for the ONE
   * selected analysis (deriveChips / loadInsightIndex).
   */
  async listCompletedAnalyses(): Promise<AdCreativeAnalysisOption[]> {
    const docs = await this.service.list({ limit: 50 });
    return docs
      .filter((doc) => doc.status === 'completed')
      .map((doc) => ({
        id: doc.id,
        label: doc.name || doc.versionLabel || doc.id,
        insightCount: countInsightsLight(doc),
        updatedAt: typeof doc.updatedAt === 'string' ? doc.updatedAt : undefined,
      }))
      .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
  }

  /**
   * All aggregated insights of one analysis, sorted by weight. Reads inline
   * aggregatedInsights first; falls back to the category_packages
   * subcollection (large analyses store insights there — same fallback the
   * backend loader uses).
   */
  private async loadInsights(analysisId: string): Promise<{ doc: InsightsAnalysisDoc | null; insights: AggregatedInsightLike[] }> {
    const doc = await this.service.get(analysisId);
    if (!doc) return { doc: null, insights: [] };
    let insights = flattenInsights(doc.aggregatedInsights);
    if (insights.length === 0) {
      const packages = await this.service.listCategoryPackages(analysisId).catch(() => []);
      const merged: Record<string, unknown[]> = {};
      for (const pkg of packages) {
        const aggregated = (pkg as { aggregatedInsights?: Record<string, unknown[]> }).aggregatedInsights ?? {};
        for (const [category, list] of Object.entries(aggregated)) {
          if (Array.isArray(list)) merged[category] = [...(merged[category] ?? []), ...list];
        }
      }
      insights = flattenInsights(merged);
    }
    return { doc, insights };
  }

  /** Chip preview from one analysis (see class doc — backend derives authoritatively). */
  /**
   * Derived context chips. With a MANUAL thesis selection the chips derive
   * ONLY from the selected theses — a category absent from the selection
   * leaves its chip empty (unselected analysis content never leaks into the
   * batch context). Auto mode keeps the analysis-wide top-of-category rule.
   */
  async deriveChips(analysisId: string, selectedInsightIds?: string[]): Promise<AdCreativeDerivedChips> {
    const { doc, insights } = await this.loadInsights(analysisId);
    if (!doc) return {};
    const manualIds = (selectedInsightIds ?? []).map(String).filter(Boolean);
    const pool = manualIds.length > 0
      ? insights.filter((entry) => entry.id && manualIds.includes(String(entry.id)))
      : insights;
    const topOf = (categories: string[]): AggregatedInsightLike | undefined =>
      pool.find((entry) => categories.includes(String(entry.category)));
    const pain = topOf(['fears']);
    const job = topOf(['coreJobs', 'bigJobs']);
    return {
      audienceName: manualIds.length > 0
        ? dominantAudienceNameForSelection(doc, pool)
        : dominantAudienceName(doc),
      painOrNeed: pain?.canonicalDescription,
      jobToBeDone: job?.canonicalDescription,
    };
  }

  /**
   * FR3: audience options for the «from insights ▾» field menu — distinct
   * audience names across the analysis's selected actors, frequency-ordered.
   */
  async listAudienceOptions(analysisId: string): Promise<string[]> {
    const { doc } = await this.loadInsights(analysisId);
    if (!doc) return [];
    const counts = new Map<string, number>();
    for (const ref of doc.selectedActorRefs ?? []) {
      const name = typeof ref?.audienceName === 'string' ? ref.audienceName.trim() : '';
      if (!name) continue;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name)
      .slice(0, 8);
  }

  /** insightId -> cite-block payload (angle cards, scene hints, coverage). */
  async loadInsightIndex(analysisId: string): Promise<AdCreativeInsightIndex> {
    const { insights } = await this.loadInsights(analysisId);
    const index: AdCreativeInsightIndex = {};
    for (const entry of insights) {
      if (!entry.id || !entry.canonicalDescription) continue;
      const topSource = (entry.sources ?? [])
        .slice()
        .sort((a, b) => (b.originalWeight ?? 0) - (a.originalWeight ?? 0))[0];
      index[String(entry.id)] = {
        category: String(entry.category ?? ''),
        thesis: entry.canonicalDescription,
        actorCount: entry.actorCount ?? entry.frequency ?? 0,
        cumulativeWeight: entry.cumulativeWeight ?? 0,
        topQuote: topSource?.originalDescription,
      };
    }
    return index;
  }
}

/** Most frequent audienceName across the analysis's selected actors. */
function dominantAudienceName(doc: InsightsAnalysisDoc): string | undefined {
  const counts = new Map<string, number>();
  for (const ref of doc.selectedActorRefs ?? []) {
    if (!ref?.audienceName) continue;
    counts.set(ref.audienceName, (counts.get(ref.audienceName) ?? 0) + 1);
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  return top?.[0];
}

/**
 * Dominant audience across ONLY the selected theses' sources (manual mode).
 * Source rows carry audienceName/actorId in real analyses; actorId falls back
 * through the analysis actor refs. No resolvable audience → undefined (the
 * chip stays empty rather than leaking the analysis-wide audience).
 */
function dominantAudienceNameForSelection(
  doc: InsightsAnalysisDoc,
  selected: AggregatedInsightLike[],
): string | undefined {
  const nameByActor = new Map<string, string>();
  for (const ref of doc.selectedActorRefs ?? []) {
    const actorId = (ref as { actorId?: string })?.actorId;
    if (actorId && ref?.audienceName) nameByActor.set(actorId, ref.audienceName);
  }
  const counts = new Map<string, number>();
  for (const entry of selected) {
    for (const source of entry.sources ?? []) {
      const name = source.audienceName ?? (source.actorId ? nameByActor.get(source.actorId) : undefined);
      if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  return top?.[0];
}
